import katex from 'katex';

/**
 * Renders the lightweight markdown used by sketches' `science` write-ups into
 * HTML for the help modal. Supports just enough to match the blog style:
 *
 *   - `$inline$` and `$$display$$` math via KaTeX
 *   - `## Heading` / `### Subheading`
 *   - `- ` bullet lists
 *   - `> ` blockquote callouts (a leading `**Label:**` becomes the callout tag)
 *   - `**bold**`, `*italic*`, `` `code` ``
 *   - blank-line-separated paragraphs
 *
 * Plain-text blurbs (the Sandbox sketches) contain none of these tokens, so
 * they render exactly as before: one `<p>` per blank-line-separated block.
 */
export function renderScience(src: string): string {
  // 1. Pull math out first so markdown/HTML escaping can't mangle the TeX.
  //    Placeholders are tagged so no inline rule or escaping touches them.
  const math: { tex: string; display: boolean }[] = [];
  const stash = (tex: string, display: boolean): string => {
    math.push({ tex: tex.trim(), display });
    return `@@MATH${math.length - 1}@@`;
  };
  const text = src
    .replace(/\$\$([\s\S]+?)\$\$/g, (_, tex) => stash(tex, true))
    .replace(/\$([^$\n]+?)\$/g, (_, tex) => stash(tex, false));

  // 2. Block-level parse on the (math-free) text.
  const html = text
    .split(/\n{2,}/)
    .map(renderBlock)
    .join('\n');

  // 3. Swap math placeholders for rendered KaTeX.
  return html.replace(/@@MATH(\d+)@@/g, (_, i) => {
    const m = math[Number(i)];
    try {
      return katex.renderToString(m.tex, { displayMode: m.display, throwOnError: false });
    } catch {
      return escapeHtml(m.tex);
    }
  });
}

function renderBlock(block: string): string {
  const lines = block.split('\n');

  // Heading: a single `##`/`###`/`####` line.
  const h = lines[0].match(/^(#{2,4})\s+(.*)$/);
  if (h && lines.length === 1) {
    const level = h[1].length + 2; // ## -> h4, ### -> h5, #### -> h6
    return `<h${level}>${inline(h[2])}</h${level}>`;
  }

  // Bullet list: every line starts with `- `.
  if (lines.every((l) => /^\s*-\s+/.test(l))) {
    const items = lines.map((l) => `<li>${inline(l.replace(/^\s*-\s+/, ''))}</li>`).join('');
    return `<ul>${items}</ul>`;
  }

  // Blockquote callout: every line starts with `>`.
  if (lines.every((l) => /^\s*>\s?/.test(l))) {
    let body = lines.map((l) => l.replace(/^\s*>\s?/, '')).join(' ');
    let label = '';
    const m = body.match(/^\*\*(.+?):\*\*\s*/);
    if (m) {
      label = `<strong class="callout-label">${inline(m[1])}</strong> `;
      body = body.slice(m[0].length);
    }
    return `<blockquote>${label}${inline(body)}</blockquote>`;
  }

  // Default: a paragraph (soft-wrapped newlines collapse to spaces).
  return `<p>${inline(lines.join(' '))}</p>`;
}

/** Inline formatting on a fragment that has already had math extracted. */
function inline(s: string): string {
  return escapeHtml(s)
    .replace(/`([^`]+?)`/g, (_, c) => `<code>${c}</code>`)
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
